import React from 'react';
import { ArrowLeft, Printer, Download } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { QuotationData } from '../quotation';
import mangrishLogo from '../../assets/logo.jpeg';
import princePipesLogo from '../../assets/products-banner.jpeg';
import princePipesLogo1 from '../../assets/product-banner.jpeg';

interface QuotationPreviewProps {
  data: QuotationData;
  onBack: () => void;
}

const QuotationPreview: React.FC<QuotationPreviewProps> = ({ data, onBack }) => {
  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    try {
      const element = document.getElementById('quotation-document');
      if (!element) return;

      // Hide action buttons before capturing
      const actionButtons = document.querySelector('.print\\:hidden');
      if (actionButtons) {
        (actionButtons as HTMLElement).style.display = 'none';
      }

      const canvas = await html2canvas(element, {
        // scale: 2,
        useCORS: true,
        allowTaint: true,
        // backgroundColor: '#ffffff',
        width: element.scrollWidth,
        height: element.scrollHeight,
      });

      // Show action buttons again
      if (actionButtons) {
        (actionButtons as HTMLElement).style.display = 'flex';
      }

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 0;

      pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);
      pdf.save(`Quotation_${data.quotationNo}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Action Buttons */}
      <div className="flex justify-between items-center mb-6 print:hidden">
        <button
          onClick={onBack}
          className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
        >
          <ArrowLeft size={16} className="mr-2" />
          Back to Form
        </button>
        {/* <div className="flex space-x-2">
          <button
            onClick={handlePrint}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Printer size={16} className="mr-2" />
            Print
          </button>
          <button
            onClick={handleDownloadPDF}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Download size={16} className="mr-2" />
            Download PDF
          </button>
        </div> */}
      </div>

      {/* Quotation Document */}
      <div className="bg-white p-8 shadow-lg print:shadow-none print:p-0" id="quotation-document">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center items-center mb-4">
            {/* Left Logo - Based on GST Number */}
            <div className="mr-6">
              <img
                src={data.gstNumber === "29ACAFM2729C1ZG" ? princePipesLogo1 : princePipesLogo1}
                alt="Left Logo"
                className="w-40 h-40 object-contain"
              />
            </div>
            
            {/* Center - Mangrish Logo and Company Info */}
            <div className="flex-1">
              <div className="flex justify-center mb-2">
                <img
                  src={mangrishLogo}
                  alt="Mangrish Logo"
                  className="w-20 h-20 object-contain"
                />
              </div>
              <h1 className="text-2xl font-bold text-gray-800 mb-2">M/s. MANGRISH</h1>
              <p className="text-sm text-gray-600">
                No. 7/4, 60 feet Road, Gangaborewell Road, Amco Layout,<br />
                Kodigehalli, Bangalore 560097
              </p>
              <p className="text-sm text-gray-600 mt-2">
                Authorised Distributor - M/s Prince Pipes and Fittings Ltd.
              </p>
              <p className="text-sm text-gray-600">
                Ph No: 7090456618 | Email: mangrish123@gmail.com, santosh@mangrishprince.com
              </p>
            </div>
            
            {/* Right Logo - Based on GST Number */}
            <div className="ml-6">
              <img
                src={data.gstNumber === "29ACAFM2729C1ZG" ? princePipesLogo : princePipesLogo}
                alt="Right Logo"
                className="w-40 h-40 object-contain"
              />
            </div>
          </div>
        </div>

        {/* Quotation Title */}
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-800 border-b-2 border-gray-300 inline-block px-8 pb-2">
            QUOTATION
          </h2>
        </div>

        {/* Company Info Repeated
        <div className="text-center mb-8 bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-bold text-gray-800 mb-2">M/s. MANGRISH</h3>
          <p className="text-sm text-gray-600">
            No. 7/4, 60 feet Road, Gangaborewell Road, Amco Layout,<br />
            Kodigehalli, Bangalore 560097
          </p>
          <p className="text-sm text-gray-600 mt-1">
            Authorised Distributor - M/s Prince Pipes and Fittings Ltd.
          </p>
          <p className="text-sm text-gray-600">
            Ph No: 7090456618 | Email: mangrish123@gmail.com, santosh@mangrishprince.com
          </p>
        </div> */}

        {/* Three Column Layout */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Bill To */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="font-bold text-gray-800 mb-3 border-b border-blue-200 pb-2">Bill To</h3>
            <p className="text-sm">
              <strong>{data.billTo.name}</strong><br />
              {data.billTo.address}<br />
              Phone: {data.billTo.phone}<br />
              Email: {data.billTo.email}
            </p>
          </div>

          {/* Ship To */}
          <div className="bg-green-50 p-4 rounded-lg">
            <h3 className="font-bold text-gray-800 mb-3 border-b border-green-200 pb-2">Ship To</h3>
            <p className="text-sm">
              <strong>{data.shipTo.name}</strong><br />
              {data.shipTo.address}<br />
              Phone: {data.shipTo.phone}<br />
              Email: {data.shipTo.email}
            </p>
          </div>

          {/* Details */}
          <div className="bg-orange-50 p-4 rounded-lg">
            <h3 className="font-bold text-gray-800 mb-3 border-b border-orange-200 pb-2">Details</h3>
            <p className="text-sm">
              <strong>Date:</strong> {data.date}<br />
              <strong>QT No:</strong> {data.quotationNo}
            </p>
          </div>
        </div>

        {/* Products Table */}
        <div className="mb-8">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 p-3 text-left font-semibold">Product</th>
                  <th className="border border-gray-300 p-3 text-center font-semibold">HSN Code</th>
                  <th className="border border-gray-300 p-3 text-center font-semibold">Qty</th>
                  <th className="border border-gray-300 p-3 text-center font-semibold">Unit</th>
                  <th className="border border-gray-300 p-3 text-right font-semibold">MRP</th>
                  <th className="border border-gray-300 p-3 text-center font-semibold">Disc%</th>
                  <th className="border border-gray-300 p-3 text-right font-semibold">Rate</th>
                  <th className="border border-gray-300 p-3 text-right font-semibold">Amount</th>
                </tr>
              </thead>
              <tbody>
                {data.products.map((product, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="border border-gray-300 p-3">
                      <div className="font-medium">{product.name}</div>
                      <div className="text-xs text-gray-500">
                        GST: {product.gstpercentage}%
                      </div>
                    </td>
                    <td className="border border-gray-300 p-3 text-center">{product.hsncode}</td>
                    <td className="border border-gray-300 p-3 text-center">{product.qty}</td>
                    <td className="border border-gray-300 p-3 text-center">{product.unit}</td>
                    <td className="border border-gray-300 p-3 text-right">₹{product.mrp.toFixed(2)}</td>
                    <td className="border border-gray-300 p-3 text-center">
                      {product.discount}%
                    </td>
                    <td className="border border-gray-300 p-3 text-right">₹{product.rate.toFixed(2)}</td>
                    <td className="border border-gray-300 p-3 text-right font-medium">₹{product.amount.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Totals */}
        <div className="flex justify-end mb-8">
          <div className="bg-gray-50 p-4 rounded-lg min-w-64">
            {data.productTotal !== undefined && (
              <div className="flex justify-between mb-2 text-sm">
                <span>Product Total:</span>
                <span className="font-medium">₹{data.productTotal.toFixed(2)}</span>
              </div>
            )}
            {data.discountAmount !== undefined && data.discountAmount > 0 && (
              <div className="flex justify-between mb-2 text-sm">
                <span>Discount ({data.discount || 0}%):</span>
                <span className="font-medium text-green-600">-₹{data.discountAmount.toFixed(2)}</span>
              </div>
            )}
            {data.transportation !== undefined && data.transportation > 0 && (
              <div className="flex justify-between mb-2 text-sm">
                <span>Transportation:</span>
                <span className="font-medium">₹{data.transportation.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between mb-2 text-sm border-t pt-2 border-gray-300">
              <span>Sub Total:</span>
              <span className="font-medium">₹{data.subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between mb-2 text-sm">
              <span>GST {data.gst}%:</span>
              <span className="font-medium">₹{data.gstAmount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-lg font-bold border-t pt-2 border-gray-300">
              <span>Total:</span>
              <span>₹{data.total.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Note */}
        {data.note && (
          <div className="mb-8 bg-yellow-50 p-4 rounded-lg">
            <h3 className="font-bold text-gray-800 mb-2 border-b border-yellow-200 pb-2">Note</h3>
            <p className="text-sm text-gray-700">{data.note}</p>
          </div>
        )}

        {/* Terms & Conditions */}
        <div className="mb-8">
          <h3 className="font-bold text-gray-800 mb-3 border-b border-gray-300 pb-2">Terms & Conditions</h3>
          <ul className="text-sm space-y-1">
            {data.terms.map((term, index) => (
              <li key={index} className="flex items-start">
                <span className="mr-2 text-gray-500">{index + 1}.</span>
                <span>{term}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Bank Details */}
        <div className="bg-blue-50 p-4 rounded-lg">
          <h3 className="font-bold text-gray-800 mb-3 border-b border-blue-200 pb-2">Bank Details</h3>
          <div className="text-sm">
            <p><strong>Payment favoring to:</strong> {data.bank.name}</p>
            <p><strong>Bank Name:</strong> CENTRAL BANK OF INDIA</p>
            <p><strong>A/C No:</strong> {data.bank.accountNo}</p>
            <p><strong>Branch:</strong> {data.bank.branch}</p>
            <p><strong>IFSC Code:</strong> {data.bank.ifsc}</p>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 pt-4 border-t border-gray-300">
          <p className="text-sm text-gray-600">
            This is a computer-generated quotation and does not require a signature.
          </p>
        </div>
      </div>
    </div>
  );
};

export default QuotationPreview;